import { useCallback, useEffect, useState } from 'react';
import type { AssistantLayout } from '../types';

export type UseAssistantPanelOptions = {
  layout?: AssistantLayout;
  defaultExpanded?: boolean;
  mobileBreakpoint?: number;
};

export type AssistantPanelState = {
  isMobile: boolean;
  isOverlay: boolean;
  isExpanded: boolean;
  setExpanded: (value: boolean) => void;
  toggleExpanded: () => void;
};

export function useAssistantPanel({
  layout = 'overlay',
  defaultExpanded = false,
  mobileBreakpoint = 767,
}: UseAssistantPanelOptions = {}): AssistantPanelState {
  const [isMobile, setIsMobile] = useState(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(() => (
    layout === 'overlay' ? defaultExpanded : true
  ));

  const isOverlay = layout === 'overlay' || isMobile;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const query = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`);
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, [mobileBreakpoint]);

  useEffect(() => {
    if (!isOverlay) {
      setIsExpanded(true);
      return;
    }
    if (layout === 'sidebar') {
      setIsExpanded(defaultExpanded);
    }
  }, [defaultExpanded, isOverlay, layout]);

  const toggleExpanded = useCallback(() => {
    if (!isOverlay) {
      return;
    }
    setIsExpanded((prev) => !prev);
  }, [isOverlay]);

  return {
    isMobile,
    isOverlay,
    isExpanded,
    setExpanded: setIsExpanded,
    toggleExpanded,
  };
}
